import { formatSlotTime } from "@/lib/scheduling/format"
import { isAxiosError } from "axios"
import {
  errorMessage,
  translateErrorCode,
  translateErrorCodeByCategory,
} from "@/lib/i18n/error-messages"

/**
 * Localized generic rejection message. A function (not a const) so it always
 * reflects the active locale.
 */
export function genericRejectionMessage(): string {
  return errorMessage("genericRejection")
}

/**
 * Resolves the customer-facing message for a rejected validation:
 * stable code first (translated via the i18n error tables), then a step-level
 * fallback so the customer still learns WHICH part failed, then the generic
 * message. Never matches on the free-text reason.
 */
export function readValidSlots(details: Record<string, unknown> | undefined): string[] {
  const raw = details?.validSlots
  if (!Array.isArray(raw)) return []
  return raw.filter((slot): slot is string => typeof slot === "string")
}

/**
 * Available-times grid ORD-CHK-4033 sends back so the customer is not left
 * guessing which times the store offers.
 */
export function formatValidSlots(slots: string[], timeZone?: string): string | null {
  const times = slots.map((slot) => formatSlotTime(slot, timeZone)).filter((time) => time.length > 0)
  if (times.length === 0) return null
  return `Horários disponíveis: ${times.join(", ")}.`
}

export function messageForRejectedValidation(
  validation: {
    step: string
    code?: string
    details?: Record<string, unknown>
  },
  timeZone?: string,
): string {
  const byCode = translateErrorCode(validation.code)
  if (byCode) {
    const slots = formatValidSlots(readValidSlots(validation.details), timeZone)
    return slots ? `${byCode} ${slots}` : byCode
  }

  switch (validation.step.trim().toLowerCase()) {
    case "coupon":
      return errorMessage("stepCoupon")
    case "loyalty":
      return errorMessage("stepLoyalty")
    case "merchant":
      return errorMessage("stepMerchant")
    case "stock":
      return errorMessage("stepStock")
    default:
      return translateErrorCodeByCategory(validation.code) ?? genericRejectionMessage()
  }
}

export type CheckoutFailure = {
  message: string
  /** True when the cart no longer matches the catalog and must be rebuilt. */
  invalidOrder: boolean
  /** Stable reason code from the gateway body, when it sent one. */
  code: string | null
  /** Delivery fee (BRL) the core quoted when it refused a changed fee (ORD-CHK-4022). */
  quotedDeliveryFee?: number
}

function readMetadata(data: unknown): Record<string, string> {
  const raw = (data as { metadata?: unknown } | undefined)?.metadata
  if (!raw || typeof raw !== "object") return {}
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
  )
}

const formatBRL = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })

function reopeningSuffix(metadata: Record<string, string>): string {
  const at = metadata.next_opening_at ? new Date(metadata.next_opening_at) : null
  if (!at || Number.isNaN(at.getTime())) return ""
  const time = at.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  return ` Volta a receber pedidos às ${time}.`
}

/** Enriches a guard refusal (closed, paused, fee changed) with the details the core sent in the error metadata. */
function withGuardDetails(failure: CheckoutFailure, metadata: Record<string, string>): CheckoutFailure {
  switch (failure.code) {
    case "ORD-CHK-4003":
    case "ORD-CHK-4036":
      return { ...failure, message: failure.message + reopeningSuffix(metadata) }
    case "ORD-CHK-4022": {
      const cents = Number(metadata.quoted_fee_cents)
      if (!Number.isFinite(cents)) return failure
      const fee = cents / 100
      return {
        ...failure,
        quotedDeliveryFee: fee,
        message: `A taxa de entrega mudou para ${formatBRL(fee)}. Confira o novo total e confirme de novo.`,
      }
    }
    default:
      return failure
  }
}

/**
 * Maps a checkout submission error (the POST itself, before the validation
 * saga) to a customer-facing failure, branching on the HTTP status code and
 * on the stable `code` field the gateway returns in its error body.
 */
export function describeCheckoutError(error: unknown): CheckoutFailure {
  if (!isAxiosError(error)) {
    return { message: errorMessage("genericRetry"), invalidOrder: false, code: null }
  }

  if (!error.response) {
    return { message: errorMessage("networkError"), invalidOrder: false, code: null }
  }

  const status = error.response.status
  const code: string | null = error.response.data?.code ?? null
  const byCode = translateErrorCode(code)
  if (byCode) {
    return withGuardDetails({ message: byCode, invalidOrder: false, code }, readMetadata(error.response.data))
  }

  switch (true) {
    case status === 400:
      return { message: errorMessage("staleCart"), invalidOrder: true, code }
    case status === 401:
      return { message: errorMessage("sessionExpired"), invalidOrder: false, code }
    case status === 409:
      return { message: errorMessage("duplicateOrder"), invalidOrder: false, code }
    case status === 422:
      return { message: genericRejectionMessage(), invalidOrder: false, code }
    case status === 429:
      return { message: errorMessage("tooManyAttempts"), invalidOrder: false, code }
    case status >= 500:
      return { message: errorMessage("serviceUnstable"), invalidOrder: false, code }
    default:
      return {
        message: translateErrorCodeByCategory(code) ?? errorMessage("genericRetry"),
        invalidOrder: false,
        code,
      }
  }
}
